/* eslint-disable react/jsx-no-bind */
/* eslint-disable no-param-reassign */
/* eslint-disable prefer-destructuring */
import PropTypes from 'prop-types';
import {
    useCallback, useEffect, useRef, useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import smoothscroll from 'smoothscroll-polyfill';

import Button from 'Component/Button';
import { SIZE_NONE, TYPE_TEXT, WIDTH_HUG } from 'Component/Button/Button.config';
import ChevronIcon from 'Component/ChevronIcon';
import { LEFT, RIGHT } from 'Component/ChevronIcon/ChevronIcon.config';
import { BLACK_KEY, M_KEY } from 'Component/Icon/Icon.config';
import { event } from 'Store/GoogleTagManager/GoogleTagManager.action';

import {
    AUTOPLAY_INTERVAL_MS,
    AUTOPLAY_SCROLL_STEP,
    DRAG_SCROLL_MULTIPLIER,
    MASK_GRADIENT_STOP,
    SCROLL_DEBOUNCE_MS,
    SCROLL_EPS
} from './ScrollSwiper.config';

import './ScrollSwiper.style.scss';

/** @namespace Bodypwa/Component/ScrollSwiper/Component/ScrollSwiper */
export const ScrollSwiper = ({
    children,
    isAutoScroll = false,
    isControls = undefined,
    isHideNavigationButton = false,
    className = '',
    gtmEvents = {},
    gtmData = { scrollEvent: {} },
    swiperWrapperClass = 'swiper-wrapper',
    isMask = false,
    onScrolled = () => { }

}) => {
    const sliderRef = useRef(null);
    const containerRef = useRef(null);

    const [hideNext, setHideNext] = useState(false);
    const [hidePrev, setHidePrev] = useState(true);
    const [hasOverflow, setHasOverflow] = useState(false);
    const isMobile = useSelector((state) => state.ConfigReducer.device.isMobile);
    const dispatch = useDispatch();
    const autoplayRef = useRef(null);
    const debounceRef = useRef(null);
    const rafRef = useRef(null);
    const gtmEvent = (eventName = '', customData) => dispatch(event(eventName, customData));

    const updateScrollButtons = useCallback(() => {
        const el = sliderRef.current;
        if (!el) {
            return;
        }

        const atEnd = Math.ceil(el.scrollLeft + el.clientWidth) >= (el.scrollWidth - SCROLL_EPS);
        const atStart = Math.floor(el.scrollLeft) <= 0;

        setHideNext(atEnd);
        setHidePrev(atStart);
    }, []);

    const slide = useCallback((direction, range) => {
        const el = sliderRef.current;
        if (!el) {
            return;
        }

        const offset = range || el.clientWidth;
        el.scroll({
            top: 0,
            left: direction === 'next' ? el.scrollLeft + offset : el.scrollLeft - offset,
            behavior: 'smooth'
        });
    }, []);

    const toggleAutoPlay = useCallback((toggle, direction) => {
        if (!isAutoScroll || isMobile) {
            return;
        }

        if (autoplayRef.current) {
            clearInterval(autoplayRef.current);
            autoplayRef.current = null;
        }

        if (toggle) {
            autoplayRef.current = setInterval(() => {
                slide(direction === 'right' ? 'next' : 'prev', AUTOPLAY_SCROLL_STEP);
            }, AUTOPLAY_INTERVAL_MS);
        }
    }, [isAutoScroll, isMobile, slide]);

    const addDragScroll = useCallback((elem) => {
        if (!elem) {
            return () => { };
        }

        let isDown = false;
        let startX;
        let scrollLeft;

        const onMouseDown = (e) => {
            e.preventDefault();
            isDown = true;
            startX = e.pageX - elem.offsetLeft;
            scrollLeft = elem.scrollLeft;
        };

        const onMouseMove = (e) => {
            if (!isDown) {
                return;
            }
            e.preventDefault();
            const x = e.pageX - elem.offsetLeft;
            const walk = (x - startX) * DRAG_SCROLL_MULTIPLIER;
             
            elem.scrollLeft = scrollLeft - walk;
        };

        const stop = () => {
            isDown = false;
        };

        elem.addEventListener('mousedown', onMouseDown);
        elem.addEventListener('mousemove', onMouseMove);
        elem.addEventListener('mouseup', stop);
        elem.addEventListener('mouseleave', stop);

        return () => {
            elem.removeEventListener('mousedown', onMouseDown);
            elem.removeEventListener('mousemove', onMouseMove);
            elem.removeEventListener('mouseup', stop);
            elem.removeEventListener('mouseleave', stop);
        };
    }, []);

    const recomputeOverflow = useCallback(() => {
        const slider = sliderRef.current;
        const container = containerRef.current;
        if (!slider || !container) {
            return;
        }

        setHasOverflow(slider.scrollWidth > container.clientWidth + SCROLL_EPS);
        updateScrollButtons();
    }, [updateScrollButtons]);

    useEffect(() => {
        smoothscroll.polyfill();

        const slider = sliderRef.current;
        if (!slider) {
            return () => { };
        }

        const onScroll = () => {
            // Batch visual updates with RAF
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
            rafRef.current = requestAnimationFrame(() => {
                updateScrollButtons();
                onScrolled(slider.scrollLeft);
            });

            // Debounce GTM event after scroll end
            if (!isMobile && gtmEvents.scrollEvent && gtmData[gtmEvents.scrollEvent]) {
                if (debounceRef.current) {
                    clearTimeout(debounceRef.current);
                }
                debounceRef.current = setTimeout(() => {
                    gtmEvent(gtmEvents.scrollEvent, gtmData[gtmEvents.scrollEvent]);
                }, SCROLL_DEBOUNCE_MS);
            }
        };

        slider.addEventListener('scroll', onScroll, { passive: true });
        updateScrollButtons();

        const cleanupDrag = addDragScroll(slider);

        return () => {
            slider.removeEventListener('scroll', onScroll);
            cleanupDrag?.();
        };
    }, [addDragScroll, gtmEvent, gtmData, gtmEvents, isMobile, onScrolled, updateScrollButtons]);

    // Watch size changes of both container & slider contents
    useEffect(() => {
        recomputeOverflow();

        const slider = sliderRef.current;
        const container = containerRef.current;
        if (!slider || !container) {
            return () => { };
        }

        let ro;
        const onResize = () => recomputeOverflow();

        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(onResize);
            ro.observe(container);
            ro.observe(slider);
        } else {
            window.addEventListener('resize', onResize);
        }

        return () => {
            ro?.disconnect();
            window.removeEventListener('resize', onResize);
        };
    }, [recomputeOverflow]);

    useEffect(() => () => {
        if (autoplayRef.current) {
            clearInterval(autoplayRef.current);
        }
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
        }
    }, []);

    const classHideButton = isHideNavigationButton ? 'hidden!' : '';
    const showControls = (isControls !== undefined) ? isControls : hasOverflow;
    const hasMask = isMask && !isMobile && hasOverflow && !hideNext;
    const maskStyle = hasMask
        ? { maskImage: `linear-gradient(to right, black ${MASK_GRADIENT_STOP}, transparent)` }
        : undefined;

    return (
        <div className={ `navigation-container  ${className}` }>
            <div
                ref={ containerRef }
                className="swiper-container swiper-container-initialized swiper-container-horizontal no-scrollbar"
                style={ maskStyle }
            >
                <div
                    ref={ sliderRef }
                    className={ `no-scrollbar flex relative cursor-pointer box-content z-10
                         will-change-transform overflow-x-scroll overflow-y-hidden whitespace-nowrap 
                         w-full h-full mr-0 lg:px-0 scroll ${swiperWrapperClass}` }
                >
                    { children }
                </div>
            </div>

            { showControls && (
                <>
                    { !hidePrev && (
                        <Button
                            onMouseEnter={ () => toggleAutoPlay(true, 'left') }
                            onMouseLeave={ () => toggleAutoPlay(false, 'left') }
                            className={ `swiper-button-prev ${classHideButton}` }
                            onClick={ () => slide('prev') }
                            width={ WIDTH_HUG }
                            type={ TYPE_TEXT }
                            size={ SIZE_NONE }
                            isIcon
                        >
                             <ChevronIcon color={ BLACK_KEY }
                                 direction={ LEFT } size={ M_KEY } className={ classHideButton } />
                        </Button>
                    ) }

                    { !hideNext && (
                        <Button
                            onMouseEnter={ () => toggleAutoPlay(true, 'right') }
                            onMouseLeave={ () => toggleAutoPlay(false, 'right') }
                            className={ `swiper-button-next ${classHideButton}` }
                            onClick={ () => slide('next') }
                            width={ WIDTH_HUG }
                            type={ TYPE_TEXT }
                            size={ SIZE_NONE }
                            isIcon
                        >
                             <ChevronIcon color={ BLACK_KEY } 
                                 direction={ RIGHT } size={ M_KEY } className={ classHideButton } />
                        </Button>
                    ) }
                </>
            ) }
        </div>
    );
};

ScrollSwiper.propTypes = {
    children: PropTypes.node.isRequired,
    isAutoScroll: PropTypes.bool,
    isControls: PropTypes.bool,
    isHideNavigationButton: PropTypes.bool,
    className: PropTypes.string,
    gtmEvents: PropTypes.shape({
        scrollEvent: PropTypes.string
    }),
    gtmData: PropTypes.shape({
        scrollEvent: PropTypes.shape({})
    }),
    onScrolled: PropTypes.func,
    isMask: PropTypes.bool,
    swiperWrapperClass: PropTypes.string
};

export default ScrollSwiper;
